<template>
  <div v-if="isLoading" class="">
    <Spinner />
  </div>
  <Table v-else>
    <TableHeader>
      <TableRow class="border-neutral-700">
        <TableHead>User</TableHead>
        <TableHead>Status</TableHead>
        <TableHead>Role</TableHead>
        <TableHead class="text-right" />
      </TableRow>
    </TableHeader>
    <TableBody v-if="invitations.length">
      <TableRow
        v-for="invitation in invitations"
        :key="invitation.id"
        class="border-neutral-700"
      >
        <TableCell class="font-medium">
          <div class="flex gap-3 items-center">
            <div
              class="size-[48px] border border-neutral-400 border-dashed rounded-full"
            />
            <span>{{ invitation.email }}</span>
          </div>
        </TableCell>
        <TableCell class="capitalize">
          <span v-if="invitation.is_expired">
            <Badge variant="secondary">Expired</Badge>
          </span>
          <span v-else>
            <Badge variant="ghost">{{ invitation.status }}</Badge>
          </span>
        </TableCell>
        <TableCell>
          {{
            roles?.find(role => role?.id == invitation.metadata?.role_id)?.name
          }}
        </TableCell>
        <TableCell class="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger as="span">
              <IconButton variant="ghost" size="sm">
                <MoreHorizontal />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem @click="resendInvitation(invitation.id)">
                Resend invite
              </DropdownMenuItem>
              <DropdownMenuItem @click="cancelInvitation(invitation.id)">
                Cancel invitation
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
    </TableBody>
    <TableBody v-else>
      <TableRow class="border-neutral-700">
        <TableCell class="text-center" colspan="4">
          No invitations yet
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
</template>

<script setup>
import { MoreHorizontal } from 'lucide-vue-next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ui/components/table';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@ui/components/dropdown-menu';
import { IconButton } from '@ui/components/icon-button';
import { Badge } from '@ui/components/badge';
import Spinner from '@ui/components/Spinner.vue';
import requests from '@vuex/requests.js';
import { queries } from '@/queries/index.js';

const queryClient = useQueryClient();

const { isLoading: isLoadingInvitations, data: invitations } = useQuery(
  queries.workspace.invitations({ status: ['PENDING'] })
);

const { isLoading: isLoadingRoles, data: roles } = useQuery(
  queries.workspace.roles
);

const isLoading = isLoadingInvitations || isLoadingRoles;

const { mutate: deleteInvitation } = useMutation({
  mutationFn: async id => {
    await requests.invitations.delete({ id });
  },
  onSuccess: async () => {
    await queryClient.invalidateQueries({
      queryKey: queries.workspace.invitations({ status: ['PENDING'] }).queryKey,
    });
  },
});

const { mutate: resendInvitationMutation } = useMutation({
  mutationFn: async id => {
    await requests.invitations.resend({ id });
  },
  onSuccess: async () => {
    await queryClient.invalidateQueries({
      queryKey: queries.workspace.invitations({ status: ['PENDING'] }).queryKey,
    });
  },
});

const resendInvitation = invitationId => {
  resendInvitationMutation(invitationId);
};

const cancelInvitation = invitationId => {
  deleteInvitation(invitationId);
};
</script>
